import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import { API_BASE } from '../config';
import { PrimarySectionBackdrop } from '../components/PrimarySectionBackdrop';
import { Seo } from '../components/Seo';
import { SEO } from '../constants/seoCopy';
import { KONTAKT_FORM_PATH } from '../constants/navigation';
import { SITE_BRAND } from '../constants/site';

const normalizeCategory = (value) => value.trim();

const extractCategories = (categoryValue = '') =>
  categoryValue
    .split(',')
    .map(normalizeCategory)
    .filter(Boolean);

/** Filteri usklađeni sa ponudom usluga; dugmad uvek vidljiva dok API vraća slike. */
const SERVICE_GALLERY_FILTERS = [
  'Klimatizacija',
  'Grejanje',
  'Ventilacija',
  'Hidrantske instalacije',
  'Sprinkler sistemi',
  'Odimljavanje garaža',
];

const normCat = (value) => value.trim().toLowerCase();

const imageMatchesFilter = (img, filterLabel) => {
  const want = normCat(filterLabel);
  return extractCategories(img.category).some((c) => normCat(c) === want);
};

/** Jedna kartica po projectSlug; redosled kao u odgovoru API-ja. */
function groupUnfinishedByProject(images) {
  const order = [];
  const map = new Map();
  for (const img of images) {
    const slug = img.projectSlug || img.id;
    if (!map.has(slug)) {
      map.set(slug, []);
      order.push(slug);
    }
    map.get(slug).push(img);
  }
  return order.map((slug) => ({
    slug,
    images: map.get(slug),
    cover: map.get(slug)[0],
  }));
}

function projectMatchesFilter(project, filterLabel) {
  return project.images.some((img) => imageMatchesFilter(img, filterLabel));
}

const SIZE_RANK = { large: 0, medium: 1, small: 2 };

function rankSize(size) {
  const s = String(size || 'medium').toLowerCase();
  return SIZE_RANK[s] ?? 1;
}

function sortProjectsForGrid(projects) {
  return [...projects].sort((a, b) => rankSize(a.cover.size) - rankSize(b.cover.size));
}

function sortImagesForGrid(images) {
  return [...images].sort((a, b) => rankSize(a.size) - rankSize(b.size));
}

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 767px)').matches;
}

export default function GalerijaPage() {
  const { pathname, search } = useLocation();
  const { projectSlug } = useParams();
  const isUToku = pathname.includes('/u-toku');
  const isUnfinishedDetail = Boolean(isUToku && projectSlug);
  const isUnfinishedList = isUToku && !projectSlug;
  const listSeo = isUToku ? SEO.projektiUToku : SEO.projektiZavrseni;

  const [activeCategory, setActiveCategory] = useState('Sve');
  /** Na mobilnom: koja kartica slike ima prikazan „hover“ (naslov + kategorija); klik pored zatvara. */
  const [touchOpenId, setTouchOpenId] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    setActiveCategory('Sve');
    setTouchOpenId(null);
  }, [projectSlug, isUToku]);

  useEffect(() => {
    setTouchOpenId(null);
  }, [activeCategory]);

  useEffect(() => {
    if (touchOpenId === null) return;
    const close = (e) => {
      if (!e.target.closest('[data-gallery-image-card]')) {
        setTouchOpenId(null);
      }
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [touchOpenId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    const finishedParam = isUToku ? 'false' : 'true';
    fetch(`${API_BASE}/api/gallery?finished=${finishedParam}`, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setGalleryImages(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
          setFetchError('Nije moguće učitati projekte. Proverite da li je server pokrenut.');
          setGalleryImages([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isUToku]);

  const categorySourceImages = useMemo(() => {
    if (isUnfinishedDetail) {
      return galleryImages.filter((img) => img.projectSlug === projectSlug);
    }
    return galleryImages;
  }, [galleryImages, isUnfinishedDetail, projectSlug]);

  const fromApi = useMemo(
    () => Array.from(new Set(categorySourceImages.flatMap((img) => extractCategories(img.category)))),
    [categorySourceImages]
  );
  const extras = useMemo(
    () => fromApi.filter((c) => !SERVICE_GALLERY_FILTERS.some((s) => normCat(s) === normCat(c))),
    [fromApi]
  );

  const categories = useMemo(
    () => ['Sve', ...SERVICE_GALLERY_FILTERS, ...extras.sort((a, b) => a.localeCompare(b, 'sr'))],
    [extras]
  );

  useEffect(() => {
    if (isUToku) return;
    const wanted = new URLSearchParams(search).get('category');
    if (!wanted) return;
    const normalizedWanted = normCat(wanted);
    const found = categories.find((c) => normCat(c) === normalizedWanted);
    setActiveCategory(found || 'Sve');
  }, [isUToku, search, categories]);

  const unfinishedProjects = useMemo(
    () => (isUnfinishedList ? groupUnfinishedByProject(galleryImages) : []),
    [isUnfinishedList, galleryImages]
  );

  const detailImages = useMemo(() => {
    if (!isUnfinishedDetail) return [];
    return galleryImages.filter((img) => img.projectSlug === projectSlug);
  }, [galleryImages, isUnfinishedDetail, projectSlug]);

  const filteredProjects =
    isUToku || activeCategory === 'Sve'
      ? unfinishedProjects
      : unfinishedProjects.filter((p) => projectMatchesFilter(p, activeCategory));

  const filteredImages = useMemo(() => {
    if (isUnfinishedList) return [];
    const base = isUnfinishedDetail ? detailImages : galleryImages;
    if (isUToku || activeCategory === 'Sve') return base;
    return base.filter((img) => imageMatchesFilter(img, activeCategory));
  }, [isUnfinishedList, isUnfinishedDetail, detailImages, galleryImages, activeCategory, isUToku]);

  const sortedFilteredProjects = useMemo(
    () => sortProjectsForGrid(filteredProjects),
    [filteredProjects]
  );

  const sortedGridImages = useMemo(() => sortImagesForGrid(filteredImages), [filteredImages]);

  const detailTitle = detailImages[0]?.alt || projectSlug?.replace(/-/g, ' ') || 'Projekat';

  const seoPath = pathname;
  const seoTitle = isUnfinishedDetail ? detailTitle : listSeo.title;
  const seoDescription = isUnfinishedDetail
    ? `Projekat u realizaciji: ${detailTitle}. Fotografije sa gradilišta — ${SITE_BRAND}, HVAC instalacije Prijepolje i šira Srbija.`
    : listSeo.description;

  return (
    <div
      data-testid={
        isUnfinishedDetail
          ? 'projekti-u-toku-detail-page'
          : isUToku
            ? 'projekti-u-toku-page'
            : 'projekti-zavrseni-page'
      }
      className="pt-20"
    >
      <Seo {...listSeo} path={seoPath} title={seoTitle} description={seoDescription} />
      <section className="relative py-24 bg-primary overflow-hidden" data-testid="projekti-list-header">
        <PrimarySectionBackdrop />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex max-w-2xl flex-col items-start"
          >
            {isUnfinishedDetail ? (
              <Link
                to="/projekti/u-toku"
                className="mb-5 inline-flex w-fit items-center gap-2 text-sm font-medium text-white/75 transition-colors hover:text-white"
                data-testid="projekti-u-toku-back-link"
              >
                ← Projekti u toku
              </Link>
            ) : (
              <Link
                to="/projekti"
                className="mb-5 inline-flex w-fit items-center gap-2 text-sm font-medium text-white/75 transition-colors hover:text-white"
                data-testid="projekti-back-link"
              >
                ← Projekti
              </Link>
            )}
            <h1 className="font-heading mb-6 text-4xl font-extrabold text-white sm:text-5xl lg:text-6xl">
              {isUToku ? (isUnfinishedDetail ? detailTitle : 'Projekti u toku') : 'Završeni projekti'}
            </h1>
            <p className="normal-case text-white/70 text-lg">
              {isUToku
                ? isUnfinishedDetail
                  ? 'Sve fotografije ovog projekta u realizaciji.'
                  : 'Izaberite projekat da vidite sve slike sa gradilišta.'
                : 'Pogledajte neke od naših realizovanih projekata sistema grejanja i hlađenja.'}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {fetchError && (
            <div className="mb-8 rounded-xl bg-heat/10 border border-heat/30 text-primary px-4 py-3 text-sm">
              {fetchError}
            </div>
          )}

          {!isUToku && (
            <motion.div
              className="flex flex-wrap justify-center gap-3 mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {categories.map((cat, i) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all duration-300 ${
                    activeCategory === cat
                      ? 'bg-primary text-white shadow-lg shadow-primary/30'
                      : 'bg-surface text-primary hover:bg-primary/10'
                  }`}
                  data-testid={`gallery-filter-${i}`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>
          )}

          {loading ? (
            <div className="text-center py-24 text-muted" data-testid="gallery-loading">
              Učitavanje…
            </div>
          ) : isUnfinishedList ? (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[280px]"
              layout
            >
              <AnimatePresence mode="popLayout">
                {sortedFilteredProjects.map((project, i) => {
                  const { cover, images, slug } = project;
                  const n = images.length;
                  return (
                    <motion.div
                      key={slug}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4, delay: i * 0.05 }}
                      className="group relative min-h-[280px] rounded-2xl overflow-hidden row-span-1"
                    >
                      <img
                        src={`${API_BASE}${cover.src}`}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div
                        className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-primary via-primary/50 to-transparent opacity-90"
                        aria-hidden
                      />
                      <div className="pointer-events-none absolute inset-0 z-[2] flex flex-col justify-end p-6">
                        {n > 1 && (
                          <span className="mb-2 inline-block w-fit rounded-full bg-black/45 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                            {n} slika
                          </span>
                        )}
                        <span className="mb-2 inline-block w-fit rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                          {cover.category}
                        </span>
                        <h3 className="text-lg font-semibold text-white drop-shadow-md">{cover.alt}</h3>
                        <span className="mt-3 inline-flex w-fit items-center gap-2 rounded-full border border-white/45 bg-white/20 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_8px_20px_rgba(255,255,255,0.16)] backdrop-blur-md transition-all duration-300 group-hover:border-white/70 group-hover:bg-white/30">
                          Pogledaj sve slike
                          <ArrowRight size={14} weight="bold" aria-hidden />
                        </span>
                      </div>
                      <Link
                        to={`/projekti/u-toku/${encodeURIComponent(slug)}`}
                        className="absolute inset-0 z-[3] rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                        data-testid={`gallery-project-card-${slug}`}
                      >
                        <span className="sr-only">
                          Otvori projekat {cover.alt}
                          {n > 1 ? `, ${n} slika` : ''}
                        </span>
                      </Link>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 ${
                isUToku ? 'auto-rows-[280px]' : 'grid-flow-dense auto-rows-[280px]'
              }`}
              layout
            >
              <AnimatePresence mode="popLayout">
                {sortedGridImages.map((img, i) => {
                  const overlayOpen = touchOpenId === img.id;
                  return (
                    <motion.div
                      key={img.id}
                      data-gallery-image-card
                      layout
                      role="presentation"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4, delay: i * 0.05 }}
                      className={`group relative rounded-2xl overflow-hidden select-none ${
                        isUToku
                          ? 'row-span-1 min-h-[280px]'
                          : img.size === 'large'
                            ? 'sm:col-span-2 sm:row-span-2'
                            : 'row-span-1'
                      }`}
                      onClick={(e) => {
                        if (!isMobileViewport()) return;
                        e.stopPropagation();
                        setTouchOpenId((id) => (id === img.id ? null : img.id));
                      }}
                      data-testid={`gallery-item-${img.id}`}
                    >
                      <img
                        src={`${API_BASE}${img.src}`}
                        alt={img.alt}
                        className="h-full w-full object-cover transition-transform duration-500 md:group-hover:scale-105"
                      />
                      <div
                        className={[
                          'absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-primary/95 via-primary/40 to-transparent p-6 transition-opacity duration-300',
                          'opacity-0 md:opacity-0 md:group-hover:opacity-100',
                          overlayOpen ? 'opacity-100' : '',
                        ].join(' ')}
                      >
                        <span className="mb-2 inline-block w-fit rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                          {img.category}
                        </span>
                        <h3 className="text-lg font-semibold leading-snug text-white drop-shadow-md">{img.alt}</h3>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {!loading && isUnfinishedList && sortedFilteredProjects.length === 0 && !fetchError && (
            <p className="text-center text-muted py-16">Nema projekata u toku.</p>
          )}
          {!loading && !isUnfinishedList && sortedGridImages.length === 0 && !fetchError && (
            <p className="text-center text-muted py-16">
              {isUnfinishedDetail ? 'Nema slika za ovaj projekat ili projekat ne postoji.' : 'Nema slika za izabrani filter.'}
            </p>
          )}
        </div>
      </section>

      <section className="py-20 bg-surface" data-testid="gallery-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-primary mb-6">
              Javite nam se
            </h2>
            <p className="text-muted text-lg mb-8">
              Kontaktirajte nas za ponudu ili konsultacije, odgovaramo brzo.
            </p>
            <Link
              to={KONTAKT_FORM_PATH}
              className="inline-flex items-center gap-3 bg-primary hover:bg-secondary text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300"
              data-testid="gallery-cta-contact"
            >
              Javite nam se
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
